import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, TrendingUp, ChevronLeft, ChevronRight, Eye } from 'lucide-react';
import GlassBoxBadge from '@/components/GlassBoxBadge';

export default function DiscoverPage() {
  const [projects, setProjects] = useState([]);
  const [stories, setStories] = useState([]);
  const [causes, setCauses] = useState([]);
  const [activeCause, setActiveCause] = useState(null);
  const [heroIdx, setHeroIdx] = useState(0);

  useEffect(() => {
    fetch('/data/projects.json').then(r => r.json()).then(projs => {
      setProjects(projs);
      const uniqueCauses = [...new Set(projs.map(p => p.causeArea).filter(Boolean))].slice(0, 6);
      setCauses(uniqueCauses);
    }).catch(() => {});
    fetch('/data/impact_stories.json').then(r => r.json()).then(setStories).catch(() => {});
  }, []);

  useEffect(() => {
    if (stories.length === 0) return;
    const timer = setInterval(() => setHeroIdx(i => (i + 1) % Math.min(stories.length, 5)), 5000);
    return () => clearInterval(timer);
  }, [stories]);

  const featured = stories.slice(0, 5);
  const hero = featured[heroIdx];
  const filtered = activeCause ? projects.filter(p => p.causeArea === activeCause) : projects;
  const grid = filtered.slice(0, 20);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6" data-testid="discover-page">
      {/* Featured Carousel */}
      {hero && (
        <div className="relative rounded-xl overflow-hidden mb-6 h-64 sm:h-80" data-testid="featured-carousel">
          <motion.img key={heroIdx} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.5 }} src={hero.backgroundImage} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-6">
            <div className="flex items-center gap-2 mb-2">
              <img src={hero.ngoAvatar} alt="" className="w-8 h-8 rounded-full border-2 border-white" />
              <span className="text-white text-sm font-medium">{hero.ngoName}</span>
            </div>
            <h2 className="text-white text-xl sm:text-2xl font-bold mb-2">{hero.title}</h2>
            <p className="text-white/70 text-sm mb-3">{hero.overlayText}</p>
            <Link to={`/project/${hero.projectSlug}`} className="inline-flex items-center px-4 py-2 rounded-lg bg-white/20 backdrop-blur-sm text-white text-sm font-medium hover:bg-white/30 transition-colors">
              Read Story
            </Link>
          </div>
          <div className="absolute bottom-3 right-4 flex gap-1.5">
            {featured.map((_, i) => <div key={i} className={`w-2 h-2 rounded-full transition-colors ${i === heroIdx ? 'bg-white' : 'bg-white/40'}`} />)}
          </div>
          <button onClick={() => setHeroIdx(i => (i - 1 + featured.length) % featured.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50"><ChevronLeft className="w-4 h-4" /></button>
          <button onClick={() => setHeroIdx(i => (i + 1) % featured.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-black/30 flex items-center justify-center text-white hover:bg-black/50"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}

      {/* Trending Causes */}
      <div className="flex items-center gap-2 overflow-x-auto pb-2 mb-6 scrollbar-hide" data-testid="trending-causes">
        <button onClick={() => setActiveCause(null)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${!activeCause ? 'bg-[#7C3AED] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`}>All</button>
        {causes.map(c => (
          <button key={c} onClick={() => setActiveCause(c)} className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${activeCause === c ? 'bg-[#7C3AED] text-white' : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'}`} data-testid={`cause-${c.toLowerCase().replace(/\s+/g, '-')}`}>
            <TrendingUp className="w-3.5 h-3.5" />{c}
          </button>
        ))}
      </div>

      {/* Masonry-style Grid */}
      <div className="columns-2 sm:columns-3 gap-4 space-y-4" data-testid="discover-grid">
        {grid.map((p, i) => (
          <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.03 }} className="break-inside-avoid">
            <Link to={`/project/${p.slug}`} className="block bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow group" data-testid={`discover-card-${i}`}>
              <div className="relative">
                <img src={p.heroImage} alt="" className={`w-full object-cover ${i % 3 === 0 ? 'h-48' : i % 3 === 1 ? 'h-36' : 'h-40'} group-hover:scale-105 transition-transform duration-300`} />
                <div className="absolute top-2 right-2"><GlassBoxBadge /></div>
                <div className="absolute bottom-2 left-2 flex items-center gap-1 bg-black/50 backdrop-blur-sm rounded-full px-2 py-0.5">
                  <Eye className="w-3 h-3 text-white" />
                  <span className="text-[10px] text-white font-medium">{3 + Math.floor(Math.random() * 12)} viewing</span>
                </div>
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold text-slate-900 line-clamp-2 mb-1">{p.title}</p>
                <p className="text-xs text-slate-400">{p.ngoName}</p>
                <div className="flex items-center justify-between mt-2">
                  <div className="w-16 h-1 rounded-full bg-slate-100 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-[#7C3AED] to-[#2563EB]" style={{ width: `${Math.round(p.fundingRaised / p.fundingGoal * 100)}%` }} /></div>
                  <span className="text-[10px] text-slate-400">{Math.round(p.fundingRaised / p.fundingGoal * 100)}%</span>
                </div>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  );
}
